import {useEffect, useState} from 'react'
import {useLayout} from '../../../../_metronic/layout/core'
import {
  createStockAdjustment,
  getWarehouseOptions,
  getProductOptions,
  getCurrentInventoryMetrics,
  WarehouseOption,
  ProductOption,
} from '../api'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import toast from 'react-hot-toast'
import Select from 'react-select'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {InventoryStateSummary} from '../../inventory/components/InventoryStateSummary'
import {removeIndicatorSeparator, solidSelectStyles} from '../../shared/ui/selectStyles'

// ─── Validation Schema ────────────────────────────────────────────────────────

const stockAdjustmentSchema = Yup.object().shape({
  warehouse_id: Yup.number().min(1, 'Warehouse is required').required('Warehouse is required'),
  product_id: Yup.number().min(1, 'Product is required').required('Product is required'),
  adjustment_type: Yup.string().required('Adjustment type is required'),
  quantity: Yup.number().min(1, 'Quantity must be at least 1').required('Quantity is required'),
  reason_code: Yup.string().required('Reason code is required'),
  adjustment_date: Yup.string().required('Adjustment date is required'),
  approver: Yup.string().required('Approver is required'),
})

// ─── Constants ────────────────────────────────────────────────────────────────

const ADJUSTMENT_TYPES = ['Addition', 'Removal']

const REASON_OPTIONS = [
  {value: 'audit_discrepancy', label: 'Audit Discrepancy'},
  {value: 'damaged_goods', label: 'Damaged Goods'},
  {value: 'expired_goods', label: 'Expired Goods'},
  {value: 'loss', label: 'Loss'},
  {value: 'others', label: 'Others'},
]

const APPROVER_OPTIONS = [
  {value: 'admin_user', label: 'Admin User'},
  {value: 'store_manager', label: 'Store Manager'},
  {value: 'supervisor', label: 'Supervisor'},
  {value: 'director', label: 'Director'},
  {value: 'others', label: 'Others'},
]

// ─── Types ────────────────────────────────────────────────────────────────────

interface StockAdjustmentFormValues {
  warehouse_id: number
  product_id: number
  adjustment_type: string
  quantity: number
  reason_code: string
  adjustment_date: string
  approver: string
}

type OptionType = {
  value: number | string
  label: string
}

const getTodayDate = () => new Date().toISOString().split('T')[0]

// ─── Component ────────────────────────────────────────────────────────────────

export const StockAdjustment: React.FC = () => {
  const {company} = useLayout()
  const [loading, setLoading] = useState(false)
  const [warehouseOptions, setWarehouseOptions] = useState<OptionType[]>([])
  const [productOptions, setProductOptions] = useState<OptionType[]>([])
  const [warehouse, setWarehouse] = useState<OptionType>({label: 'Select Warehouse...', value: 0})
  const [product, setProduct] = useState<OptionType>({label: 'Select Product...', value: 0})
  const [reason, setReason] = useState<OptionType>({label: 'Select Reason...', value: ''})
  const [approver, setApprover] = useState<OptionType>({label: 'Select Approver...', value: ''})
  const [availableStock, setAvailableStock] = useState<number>(0)
  const [valuationPerUnit, setValuationPerUnit] = useState<number>(0)

  const initialValues: StockAdjustmentFormValues = {
    warehouse_id: 0,
    product_id: 0,
    adjustment_type: 'Addition',
    quantity: 0,
    reason_code: '',
    adjustment_date: getTodayDate(),
    approver: '',
  }

  const formik = useFormik<StockAdjustmentFormValues>({
    initialValues,
    validationSchema: stockAdjustmentSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      if (values.adjustment_type === 'Removal' && values.quantity > availableStock) {
        toast.error('Removal quantity exceeds current stock.')
        return
      }
      setLoading(true)
      const body = {
        stock_adjustment_id: null,
        company_id: Number(company?.value) || null,
        warehouse_id: values.warehouse_id,
        product_id: values.product_id,
        quantity: values.quantity,
        adjustment_type: values.adjustment_type.toLowerCase(),
        reason_code: values.reason_code,
        adjustment_date: values.adjustment_date,
        approver: values.approver,
      }
      await createStockAdjustment(body)
        .then((res) => {
          res?.detail && toast.success('Stock Adjustment has been recorded successfully.')
          formik.resetForm()
          setWarehouse({label: 'Select Warehouse...', value: 0})
          setProduct({label: 'Select Product...', value: 0})
          setReason({label: 'Select Reason...', value: ''})
          setApprover({label: 'Select Approver...', value: ''})
          setAvailableStock(0)
          setValuationPerUnit(0)
        })
        .catch((err) => toast.error(err?.detail || err.message))
        .finally(() => setLoading(false))
    },
  })

  // Load warehouse and product options
  useEffect(() => {
    const loadOptions = async () => {
      if (!company?.value) return

      const [whRes, prodRes] = await Promise.all([
        getWarehouseOptions(company.value),
        getProductOptions(company.value),
      ])
      if (whRes) {
        setWarehouseOptions(
          whRes.map((w: WarehouseOption) => ({label: w.warehouse_name, value: w.warehouse_id}))
        )
      }
      if (prodRes) {
        setProductOptions(
          prodRes.map((p: ProductOption) => ({label: p.product_name, value: p.product_id}))
        )
      }
    }

    loadOptions()
  }, [company?.value])

  // Sync selects back into formik
  useEffect(() => {
    formik.setFieldValue('warehouse_id', warehouse.value, true)
    formik.setFieldValue('product_id', product.value, true)
    formik.setFieldValue('reason_code', reason.value, true)
    formik.setFieldValue('approver', approver.value, true)
  }, [warehouse.value, product.value, reason.value, approver.value])

  // Calculate available stock when warehouse + product both selected
  useEffect(() => {
    const calculateAvailableStock = async () => {
      if (!company?.value || !warehouse.value || !product.value) {
        setAvailableStock(0)
        setValuationPerUnit(0)
        return
      }
      try {
        const metrics = await getCurrentInventoryMetrics(
          company.value,
          Number(warehouse.value),
          Number(product.value)
        )
        if (metrics) {
          setAvailableStock(Number(metrics.available_stock || 0))
          setValuationPerUnit(Number(metrics.valuation_per_unit || 0))
        }
      } catch {
        setAvailableStock(0)
        setValuationPerUnit(0)
      }
    }
    calculateAvailableStock()
  }, [warehouse.value, product.value, company?.value])

  return (
    <KTCard>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Stock Adjustment</h3>
        </div>
      </div>

      <KTCardBody className='py-4'>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          {/* Warehouse + Product */}
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Warehouse</label>
            <div className='col-lg-4 fv-row'>
              <Select
                value={warehouse}
                onChange={(option: any) => setWarehouse(option as OptionType)}
                options={warehouseOptions}
                isSearchable={true}
                className='fw-bold'
                styles={solidSelectStyles}
                components={removeIndicatorSeparator}
              />
            </div>

            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Product</label>
            <div className='col-lg-4 fv-row'>
              <Select
                value={product}
                onChange={(option: any) => setProduct(option as OptionType)}
                options={productOptions}
                isSearchable={true}
                className='fw-bold'
                styles={solidSelectStyles}
                components={removeIndicatorSeparator}
              />
            </div>
          </div>

          {/* Current Stock (readonly display) */}
          <InventoryStateSummary
            stockLabel='Current Stock'
            availableStock={availableStock}
            valuationPerUnit={valuationPerUnit}
          />

          {/* Adjustment Type */}
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Adjustment Type</label>
            <div className='col-lg-10 fv-row d-flex gap-8 align-items-center pt-2'>
              {ADJUSTMENT_TYPES.map((t) => (
                <span key={t} className='form-check form-check-custom form-check-solid fs-6'>
                  {t}{' '}
                  <input
                    className='form-check-input'
                    type='radio'
                    name='adjustment_type'
                    value={t}
                    checked={formik.values.adjustment_type === t}
                    onChange={() => formik.setFieldValue('adjustment_type', t)}
                    style={{marginLeft: '8px'}}
                  />
                </span>
              ))}
            </div>
          </div>

          {/* Quantity + Reason */}
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Quantity</label>
            <div className='col-lg-4 fv-row'>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                placeholder='Quantity'
                {...formik.getFieldProps('quantity')}
              />
              {formik.touched.quantity && formik.errors.quantity && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.quantity}</div>
                </div>
              )}
            </div>

            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Reason Code</label>
            <div className='col-lg-4 fv-row'>
              <Select
                value={reason}
                onChange={(option: any) => setReason(option as OptionType)}
                options={REASON_OPTIONS}
                isSearchable={false}
                className='fw-bold'
                styles={solidSelectStyles}
                components={removeIndicatorSeparator}
              />
            </div>
          </div>

          {/* Date + Approver */}
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Adjustment Date</label>
            <div className='col-lg-4 fv-row'>
              <input
                type='date'
                className='form-control form-control-lg form-control-solid'
                {...formik.getFieldProps('adjustment_date')}
              />
              {formik.touched.adjustment_date && formik.errors.adjustment_date && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.adjustment_date}</div>
                </div>
              )}
            </div>

            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Approver</label>
            <div className='col-lg-4 fv-row'>
              <Select
                value={approver}
                onChange={(option: any) => setApprover(option as OptionType)}
                options={APPROVER_OPTIONS}
                isSearchable={false}
                className='fw-bold'
                styles={solidSelectStyles}
                components={removeIndicatorSeparator}
              />
            </div>
          </div>

          <div className='card-footer d-flex justify-content-start px-0 border-0 pb-0 pt-4'>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={
                loading ||
                !formik.isValid ||
                (formik.values.adjustment_type === 'Removal' &&
                  formik.values.quantity > availableStock)
              }
            >
              {!loading ? 'Adjust Stock' : null}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </KTCardBody>
    </KTCard>
  )
}
